import { FC, useEffect, useState } from 'react';

import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';

import {
  IWeb3ConsentTerm,
  acceptWeb3Consent,
} from '~/common/services/controllers/web3-consent';
import { useToast } from '~/common/providers/toast';
import { Typography } from '~/common/components/Typography';
import { Modal } from '~/common/components/Modal';
import { Checkbox } from '~/common/components/Form/Checkbox';
import { Button } from '~/common/components/Button';

import { StyMarkdownContainer, StyCheckboxRow } from './styles';

const WEB3_FEES_TERM = `## Web3 - Carteira, Caixinha e Swap

**Taxas e Encargos.** Os serviços poderão estar sujeitos à cobrança de taxas, dependendo do tipo de transação ou produto utilizado.

Para investimentos realizados por meio das estratégias da "Caixinha", será aplicada uma taxa de performance de 5% sobre o desempenho positivo gerado pela estratégia, calculada e cobrada de forma recorrente mensalmente.

Para transações de Swap realizadas por meio do CryptoControl, será aplicada uma taxa fixa de 50 pontos-base (0,50%) por transação. Adicionalmente, o provedor de bridge e/ou swap aplicável poderá cobrar uma taxa adicional, dependendo da rota selecionada, geralmente variando entre 1 ponto-base (0,01%) para rotas de USDC para USDC e até 100 pontos-base (1,00%) para rotas mais exóticas. Essas taxas de terceiros são independentes da taxa cobrada pelo CryptoControl e poderão variar de acordo com o provedor e a rota da transação.

Para transações de Ramp, envolvendo a conversão de moeda fiduciária (fiat) em criptoativos ou de criptoativos em moeda fiduciária, o provedor terceirizado do serviço de Ramp poderá cobrar uma taxa entre R$ 0,10 e R$ 0,75 por depósito ou transação, conforme aplicável.

As taxas cobradas por terceiros poderão variar dentro dos intervalos descritos acima e serão determinadas e cobradas pelos respectivos prestadores de serviço. As taxas e respectivos valores poderão sofrer alterações a qualquer momento, de acordo com o provedor, a rota selecionada, as condições da transação ou outros fatores operacionais.`;

interface IWeb3ConsentTermModalProps {
  isOpen: boolean;
  term: IWeb3ConsentTerm | null;
  onClose: () => void;
  onConsented: () => void;
}

export const Web3ConsentTermModal: FC<IWeb3ConsentTermModalProps> = ({
  isOpen,
  term,
  onClose,
  onConsented,
}) => {
  const { showToast } = useToast();
  const [checked, setChecked] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setChecked(false);
      setSaving(false);
    }
  }, [isOpen]);

  const handleDismiss = (): void => {
    onClose();
    showToast({
      type: 'warning',
      position: 'top-right',
      title: 'Consentimento necessário',
      description:
        'É necessário aceitar o termo de consentimento para acessar as opções do Módulo Web3.',
    });
  };

  const handleSave = async (): Promise<void> => {
    if (!term || !checked || saving) return;

    setSaving(true);
    try {
      await acceptWeb3Consent(term.id);
      onConsented();
    } catch {
      showToast({
        type: 'error',
        position: 'top-right',
        title: 'Erro ao salvar',
        description:
          'Houve um erro ao registrar o consentimento. Tente novamente.',
      });
      setSaving(false);
    }
  };

  return (
    <Modal
      id="web3-consent-term-modal"
      isOpen={isOpen}
      onClose={handleDismiss}
      width="640px"
      height="auto"
      title="Termo de Consentimento — Módulo Web3"
      hasCloseButton
      footer={
        <Button
          width="100%"
          text={saving ? 'Salvando...' : 'Salvar'}
          justifyContent="center"
          disabled={!checked || saving}
          onClick={handleSave}
        />
      }
    >
      <StyMarkdownContainer>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {`${term?.content || ''}\n\n${WEB3_FEES_TERM}`}
        </ReactMarkdown>
      </StyMarkdownContainer>
      <StyCheckboxRow>
        <Checkbox
          isChecked={checked}
          onToggle={() => setChecked((prev) => !prev)}
        />
        <Typography.Label size="small" color="neutral.300">
          Li e concordo com o termo de consentimento do Módulo Web3.
        </Typography.Label>
      </StyCheckboxRow>
    </Modal>
  );
};
